import React from "react";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import { useI18n } from "../../i18n";

const Terms = () => {
  const { lang, t } = useI18n();
  return (
    <>
      <Header />
      
      <section className="pt-36 pb-24 px-5 sm:px-8">
        <div className="max-w-3xl mx-auto">
          <h1 className="rise font-display text-5xl md:text-6xl leading-[1.05] mb-4 text-ink">
            {lang === "en" ? "Terms & Conditions" : t("footer.legal.terms")}
          </h1>
          {lang !== "en" && (
            <p className="mb-4 rounded-2xl border border-line bg-surface px-4 py-3 text-sm text-ink-2">{t("legal.notice")}</p>
          )}
          <p className="text-ink-3 text-sm mb-12 pb-8 border-b border-line">
            Last Updated: December 2024
          </p>

          <div className="legal" dir="ltr" lang="en">
            <section>
              <h2>1. Introduction</h2>
              <p className="text-base leading-relaxed mb-4">
                Welcome to Optivra Tech. These Terms and Conditions ("Terms") govern your use of our services and website. By accessing or using our services, you agree to be bound by these Terms. If you do not agree with any part of these Terms, please do not use our services.
              </p>
            </section>

            <section>
              <h2>2. Services Agreement</h2>
              <p className="text-base leading-relaxed mb-4">
                Optivra Tech provides IT services including but not limited to:
              </p>
              <ul className="space-y-2 ml-4 mb-4">
                <li>AI Automation Systems Development</li>
                <li>Custom Website Development</li>
                <li>Python Automation & Data Solutions</li>
                <li>AI Video & Avatar Systems</li>
                <li>Technology Consulting Services</li>
              </ul>
              <p className="text-base leading-relaxed">
                All services are provided as per the scope of work agreed upon in writing between Optivra Tech and the client. Service deliverables, timelines, and pricing will be specified in individual project agreements or service contracts.
              </p>
            </section>

            <section>
              <h2>3. Payment Terms</h2>
              <p className="text-base leading-relaxed mb-4">
                <strong>3.1 Pricing:</strong> All prices are quoted in Indian Rupees (INR) unless otherwise specified. Prices are subject to change without notice, but any changes will not affect orders already placed.
              </p>
              <p className="text-base leading-relaxed mb-4">
                <strong>3.2 Payment Schedule:</strong> Payment terms will be specified in the project agreement. Typically, projects require:
              </p>
              <ul className="space-y-2 ml-4 mb-4">
                <li>50% advance payment before project commencement</li>
                <li>Milestone-based payments as agreed</li>
                <li>Final payment upon project delivery</li>
              </ul>
              <p className="text-base leading-relaxed mb-4">
                <strong>3.3 Taxes:</strong> All prices are exclusive of applicable taxes including GST. Taxes will be added as per prevailing Indian tax laws.
              </p>
              <p className="text-base leading-relaxed">
                <strong>3.4 Late Payments:</strong> Late payments may result in project delays or suspension of services until payment is received.
              </p>
            </section>

            <section>
              <h2>4. Intellectual Property Rights</h2>
              <p className="text-base leading-relaxed mb-4">
                <strong>4.1 Client Ownership:</strong> Upon full payment, the client owns all deliverables specifically created for their project, including custom code, designs, and documentation.
              </p>
              <p className="text-base leading-relaxed mb-4">
                <strong>4.2 Optivra Tech Ownership:</strong> We retain ownership of:
              </p>
              <ul className="space-y-2 ml-4 mb-4">
                <li>Pre-existing intellectual property and frameworks</li>
                <li>Methodologies, processes, and know-how</li>
                <li>Reusable code components and libraries</li>
              </ul>
              <p className="text-base leading-relaxed">
                <strong>4.3 Third-Party Components:</strong> Projects may include third-party libraries, frameworks, or services subject to their respective licenses.
              </p>
            </section>

            <section>
              <h2>5. Confidentiality</h2>
              <p className="text-base leading-relaxed mb-4">
                Both parties agree to maintain confidentiality of all proprietary and sensitive information shared during the engagement. This obligation survives the termination of the agreement.
              </p>
              <p className="text-base leading-relaxed">
                We will not disclose client information to third parties except:
              </p>
              <ul className="space-y-2 ml-4">
                <li>With explicit client consent</li>
                <li>As required by law</li>
                <li>To subcontractors under confidentiality agreements</li>
              </ul>
            </section>

            <section>
              <h2>6. Project Timeline & Delays</h2>
              <p className="text-base leading-relaxed mb-4">
                We strive to meet agreed timelines. However, timelines are estimates and may be affected by:
              </p>
              <ul className="space-y-2 ml-4 mb-4">
                <li>Delayed client feedback or approvals</li>
                <li>Changes in project scope</li>
                <li>Unavailability of required third-party services</li>
                <li>Force majeure events</li>
              </ul>
              <p className="text-base leading-relaxed">
                We will communicate promptly about any anticipated delays and work to minimize their impact.
              </p>
            </section>

            <section>
              <h2>7. Warranties & Disclaimers</h2>
              <p className="text-base leading-relaxed mb-4">
                <strong>7.1 Service Warranty:</strong> We warrant that services will be performed with professional skill and care.
              </p>
              <p className="text-base leading-relaxed mb-4">
                <strong>7.2 Bug Fixes:</strong> We provide bug fixes for 30-60 days post-delivery as specified in the project agreement.
              </p>
              <p className="text-base leading-relaxed">
                <strong>7.3 Disclaimer:</strong> Services are provided "as is" without warranties beyond those explicitly stated. We do not guarantee specific business results or outcomes.
              </p>
            </section>

            <section>
              <h2>8. Limitation of Liability</h2>
              <p className="text-base leading-relaxed mb-4">
                To the maximum extent permitted by law, Optivra Tech's liability is limited to the amount paid by the client for the specific service giving rise to the claim.
              </p>
              <p className="text-base leading-relaxed">
                We are not liable for:
              </p>
              <ul className="space-y-2 ml-4">
                <li>Indirect, consequential, or incidental damages</li>
                <li>Loss of profits, revenue, or business opportunities</li>
                <li>Data loss or corruption (clients are responsible for backups)</li>
                <li>Third-party service failures</li>
              </ul>
            </section>

            <section>
              <h2>9. Client Responsibilities</h2>
              <p className="text-base leading-relaxed mb-4">
                Clients are responsible for:
              </p>
              <ul className="space-y-2 ml-4">
                <li>Providing timely feedback and approvals</li>
                <li>Supplying necessary content, credentials, and assets</li>
                <li>Maintaining backups of their data</li>
                <li>Compliance with applicable laws and regulations</li>
                <li>Payment of agreed fees on time</li>
              </ul>
            </section>

            <section>
              <h2>10. Termination</h2>
              <p className="text-base leading-relaxed mb-4">
                Either party may terminate the agreement with written notice. Upon termination:
              </p>
              <ul className="space-y-2 ml-4">
                <li>Client pays for work completed to date</li>
                <li>Optivra Tech delivers work completed</li>
                <li>Confidentiality obligations continue</li>
                <li>Refunds are subject to our Refund Policy</li>
              </ul>
            </section>

            <section>
              <h2>11. Governing Law & Jurisdiction</h2>
              <p className="text-base leading-relaxed mb-4">
                These Terms are governed by the laws of India. Any disputes arising from these Terms or our services shall be subject to the exclusive jurisdiction of courts in India.
              </p>
            </section>

            <section>
              <h2>12. Changes to Terms</h2>
              <p className="text-base leading-relaxed mb-4">
                We reserve the right to modify these Terms at any time. Updated Terms will be posted on our website with a revised "Last Updated" date. Continued use of our services constitutes acceptance of modified Terms.
              </p>
            </section>

            <section>
              <h2>13. Contact Information</h2>
              <p className="text-base leading-relaxed mb-2">
                For questions about these Terms, please contact us:
              </p>
              <div className="rounded-lg bg-surface border border-line p-6 mt-4">
                <p className="text-base mb-2"><strong>Optivra Tech</strong></p>
                <p className="text-base mb-2">GST: 08BNOPS7805R1ZT</p>
                <p className="text-base mb-2">Address: C-52, Amar Colony, Udasar Fanta, Jaipur Road, Bikaner, Rajasthan, India</p>
                <p className="text-base mb-2">Email: founder@optivratech.com</p>
                <p className="text-base">Phone: +91 86198 38718</p>
              </div>
            </section>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default Terms;
